// Añade '#' si el valor es un hex desnudo (sin '#'). Deja intactos rgba(), hsl(), etc.
export function normalizeCssColor(value) {
  if (!value) return value;
  const trimmed = String(value).trim();
  return /^[0-9a-fA-F]{3,8}$/.test(trimmed) ? `#${trimmed}` : trimmed;
}

export function setColorsUi(colorsUi) {
  if (colorsUi) {
    if (colorsUi.color) document.documentElement.style.setProperty('--color-first', normalizeCssColor(colorsUi.color));
    if (colorsUi.colorGradient1) document.documentElement.style.setProperty('--color-gradient-1', normalizeCssColor(colorsUi.colorGradient1));
    if (colorsUi.colorGradient2) document.documentElement.style.setProperty('--color-gradient-2', normalizeCssColor(colorsUi.colorGradient2));
    if (colorsUi.colorAlpha) document.documentElement.style.setProperty('--color-first-alpha', colorsUi.colorAlpha);
  }
}

// Snapshot inmutable del color del tenant. Lo consumen los scopes que deben
// ignorar la personalización de color del programa (mixin `tenant-color-scope`).
// Debe escribirse SOLO al aplicar el tema del tenant en el bootstrap, nunca
// desde el path del template/programa (que sí pisa --color-first globalmente).
export function setTenantColorSnapshot(colorsUi) {
  if (colorsUi) {
    if (colorsUi.color) document.documentElement.style.setProperty('--color-first-tenant', normalizeCssColor(colorsUi.color));
    if (colorsUi.colorAlpha) document.documentElement.style.setProperty('--color-first-alpha-tenant', colorsUi.colorAlpha);
    if (colorsUi.colorGradient1) document.documentElement.style.setProperty('--color-gradient-1-tenant', normalizeCssColor(colorsUi.colorGradient1));
    if (colorsUi.colorGradient2) document.documentElement.style.setProperty('--color-gradient-2-tenant', normalizeCssColor(colorsUi.colorGradient2));
  }
}

export function setFontsUi(fontsUi) {
  if (fontsUi) {
    if (fontsUi.fontFirst) document.documentElement.style.setProperty('--font-first-custom', fontsUi.fontFirst);
    if (fontsUi.fontFirstSemi) document.documentElement.style.setProperty('--font-first-semi-custom', fontsUi.fontFirstSemi);
    if (fontsUi.fontSecond) document.documentElement.style.setProperty('--font-second-custom', fontsUi.fontSecond);
    if (fontsUi.fontSecondBold) document.documentElement.style.setProperty('--font-second-bold-custom', fontsUi.fontSecondBold);
  }
}

export function setMintFonts(fontsUi) {
  if (fontsUi?.fontSecond) {
    document.documentElement.style.setProperty('--mint-brand-font-first', fontsUi.fontSecond);
    document.documentElement.style.setProperty('--mint-brand-font-second', fontsUi.fontSecond);
  }
}

// Fija la fuente de los controles de Mint (toolbar, botones, popups) a la del
// tenant para que la tipografía custom del programa quede solo en el contenido
// autoral. Forward-compatible con TAN-9092: hoy el paquete Mint aún no consume
// esta var, pero la dejamos escrita para cuando lo haga.
export function setMintControlsFont(fontFamily) {
  if (fontFamily) {
    document.documentElement.style.setProperty('--mint-controls-font-family', fontFamily);
  }
}

export function setExtraPropertiesUi(extraPropertiesUi) {
  if (extraPropertiesUi?.borderRadius) {
    document.documentElement.style.setProperty('--border-radius-01', extraPropertiesUi.borderRadius);
  } else {
    document.documentElement.style.removeProperty('--border-radius-01');
  }
}

export function clearCustomStyleProperties() {
  document.documentElement.style.removeProperty('--color-first');
  document.documentElement.style.removeProperty('--color-gradient-1');
  document.documentElement.style.removeProperty('--color-gradient-2');
  document.documentElement.style.removeProperty('--color-first-alpha');
  document.documentElement.style.removeProperty('--color-first-alpha-custom');
  document.documentElement.style.removeProperty('--color-first-light-custom');
  document.documentElement.style.removeProperty('--border-radius-01');
  document.documentElement.style.removeProperty('--border-radius-custom');
  document.documentElement.style.removeProperty('--mint-border-radius');
  document.documentElement.style.removeProperty('--mint-controls-font-family');
  // Tipografía custom del programa: sin esto la fuente de un programa persiste
  // al salir o al entrar en otro sin custom style (en Edelvives no hay
  // setFontsUi que la restaure). Quitarlas devuelve la fuente del tenant:
  // ambos bundles definen las -custom con fallback en su CSS estático.
  document.documentElement.style.removeProperty('--font-first-custom');
  document.documentElement.style.removeProperty('--font-first-semi-custom');
  document.documentElement.style.removeProperty('--font-second-custom');
  document.documentElement.style.removeProperty('--font-second-bold-custom');
  document.documentElement.style.removeProperty('--mint-brand-font-first');
  document.documentElement.style.removeProperty('--mint-brand-font-second');
  // Legacy: --font-first/-semi/-second/-bold ya no se escriben (la tipografía
  // custom solo vive en las vars -custom), pero seguimos limpiándolos una
  // release por si quedó residuo de sesiones previas persistidas.
  document.documentElement.style.removeProperty('--font-first');
  document.documentElement.style.removeProperty('--font-first-semi');
  document.documentElement.style.removeProperty('--font-second');
  document.documentElement.style.removeProperty('--font-second-bold');
}

function brandAlpha(color) {
  return `color-mix(in srgb, ${color} 15%, transparent)`;
}

// Tinte claro y OPACO del color de marca: para estados que lo usan como fondo
// sólido (p.ej. el botón disabled de Edelvives). El alpha translúcido NO sirve
// ahí: sobre superficies claras desaparece y deja el texto blanco ilegible.
function brandLight(color) {
  return `color-mix(in srgb, ${color} 35%, white)`;
}

// Color base de marca del programa a partir de sus stops. Con gradiente se
// mezclan los dos stops al 50%; si no hay gradiente se usa el secundario plano;
// como último recurso el primario.
function deriveBrandBaseColor(colorsUi) {
  if (colorsUi.color_second_style === 'gradient' && colorsUi.color_gradient_1 && colorsUi.color_gradient_2) {
    const gradient1 = normalizeCssColor(colorsUi.color_gradient_1);
    const gradient2 = normalizeCssColor(colorsUi.color_gradient_2);
    return `color-mix(in srgb, ${gradient1} 50%, ${gradient2})`;
  }
  if (colorsUi.color_second) {
    return normalizeCssColor(colorsUi.color_second);
  }
  if (colorsUi.color_first) {
    return normalizeCssColor(colorsUi.color_first);
  }
  return null;
}

function setCustomBorderRadius(borderRadious) {
  // Coerción: el BO puede mandar el valor con unidad ("20px" + 'px' = "20pxpx"),
  // y una var definida pero inválida NO dispara el fallback de var() — el radius
  // colapsaría a 0 en vez de volver al del tenant.
  const radius = Number.parseFloat(borderRadious);
  if (Number.isNaN(radius)) return;
  // Radius custom del programa: solo lo consumen los selectores opt-in que usan
  // $border-radius-custom (banner y contenido de módulos), NO --border-radius-01
  // (radius global del tenant, que ya no se pisa).
  document.documentElement.style.setProperty('--border-radius-custom', radius + 'px');
  document.documentElement.style.setProperty('--mint-border-radius', radius + 'px');
  // Los controles de Mint (botones, diálogos, popups) NO llevan el radius custom:
  // se pinean a sus defaults en CSS estático (_program-theming.scss, sección 3),
  // porque el paquete re-declara sus vars granulares en .mint-lesson (no en :root)
  // y un pin inline en documentElement quedaría sombreado.
}

function setCustomGradient(colorsUi) {
  if (colorsUi.color_second_style === 'gradient') {
    if (colorsUi.color_gradient_1) document.documentElement.style.setProperty('--color-gradient-1', normalizeCssColor(colorsUi.color_gradient_1));
    if (colorsUi.color_gradient_2) document.documentElement.style.setProperty('--color-gradient-2', normalizeCssColor(colorsUi.color_gradient_2));
  } else if (colorsUi.color_second) {
    document.documentElement.style.setProperty('--color-gradient-1', normalizeCssColor(colorsUi.color_second));
    document.documentElement.style.setProperty('--color-gradient-2', normalizeCssColor(colorsUi.color_second));
  }
}

export function setCustomColorsUi(colorsUi) {
  if (!colorsUi) return;
  if (colorsUi.color_first) document.documentElement.style.setProperty('--color-first', normalizeCssColor(colorsUi.color_first));
  if (colorsUi.border_radious || colorsUi.border_radious === 0) setCustomBorderRadius(colorsUi.border_radious);
  setCustomGradient(colorsUi);
  // Alpha de marca (--ui-color-brand-fod-alpha lo alias en el DS v2) para que
  // los IconBubble con fondo alpha tomen el color del programa.
  const brandBaseColor = deriveBrandBaseColor(colorsUi);
  if (brandBaseColor) {
    const colorFirstAlpha = brandAlpha(brandBaseColor);
    document.documentElement.style.setProperty('--color-first-alpha', colorFirstAlpha);
    // Variantes opt-in que SOLO existen dentro de un programa: las usan los
    // skins que marcan estados con un literal del tenant como
    // var(--color-first-*-custom, <literal>). La -alpha- es translúcida (halos,
    // IconBubble); la -light- es opaca, para fondos sólidos como el botón
    // disabled de Edelvives ($color-primary-disabled) — un fondo translúcido
    // ahí dejaría el texto blanco sin contraste.
    document.documentElement.style.setProperty('--color-first-alpha-custom', colorFirstAlpha);
    document.documentElement.style.setProperty('--color-first-light-custom', brandLight(brandBaseColor));
  }
}

export function setFontFamilyFirst(fontFamily) {
  // No se escriben --font-first/-semi (globales del tenant): la tipografía
  // custom del programa vive solo en las vars -custom (contenido autoral).
  document.documentElement.style.setProperty('--font-first-custom', fontFamily.family);
  document.documentElement.style.setProperty('--font-first-semi-custom', fontFamily.family);
  document.documentElement.style.setProperty('--mint-brand-font-first', fontFamily.family);
}

export function setFontFamilySecond(fontFamily) {
  // No se escriben --font-second/-bold (globales del tenant): la tipografía
  // custom del programa vive solo en las vars -custom (contenido autoral).
  document.documentElement.style.setProperty('--font-second-custom', fontFamily.family);
  document.documentElement.style.setProperty('--font-second-bold-custom', fontFamily.family);
  document.documentElement.style.setProperty('--mint-brand-font-second', fontFamily.family);
}

/**
 * Loads an external font using the FontFace API
 * @param {Object} fontData - The font data object
 * @param {string} fontData.guid - Unique identifier for the font
 * @param {string} fontData.name - Display name of the font
 * @param {string} fontData.fonts - Array of font files
 * @returns {Promise<FontFace>} A promise that resolves with the loaded FontFace
 */
export async function loadExternalFont(fontData) {
  try {
    // Check if FontFace API is supported
    if (!('FontFace' in window)) {
      throw new Error('FontFace API is not supported in this browser');
    }

    // Create a new FontFace instance
    const fontFace = new FontFace(fontData?.name, `url(${fontData?.url}) format('${fontData?.url.split('.').pop()}')`);

    // Load and add the font to the document fonts collection
    const loadedFont = await fontFace.load();
    document.fonts.add(loadedFont);

    return loadedFont;
  } catch (error) {
    console.error(`Error loading font "${fontData?.name}":`, error);
    throw error;
  }
}
